import { Injectable, Injector, inject } from '@angular/core';
import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import gql from 'graphql-tag';
import { filter, switchMap, take } from 'rxjs';
import { selectLoggedInGuardianId } from '../../store/guardian/guardian.selectors';

interface GuardianGroup {
  id: string;
  school: string;
  name: string;
  year: string;
  dateCreated: string;
  dateLastModified: string;
  createdBy: string;
  lastModifiedBy: string;
  status: string;
  staticGroup: boolean;
  everlyticId: string;
  synergyName: string;
  synergyType: string;
}

interface DeviceTokenResponse {
  mobileDeviceToken: {
    id: string;
    synergyId: string;
    tokenId: string;
    dateCreated: string;
    deviceDetails: string;
  };
}

export interface PushNotificationPermissionStatus {
  hasPermission: boolean;
  message: string;
  status?: 'granted' | 'denied' | 'prompt' | 'unsupported' | 'checking';
}

@Injectable({
  providedIn: 'root',
})
export class PushNofiticationsService {
  private apollo: Apollo | undefined;
  private deviceId: string | null = null;
  private listenersRegistered = false;
  private readonly store = inject(Store);
  private readonly injector = inject(Injector);

  constructor() {
    setTimeout(() => {
      this.apollo = this.injector.get(Apollo);
    });
  }

  initDeviceRegistration() {
    if (!Capacitor.isNativePlatform() || this.listenersRegistered) return;
    this.listenersRegistered = true;

    this.store
      .select(selectLoggedInGuardianId)
      .pipe(
        filter((id) => !!id),
        take(1),
      )
      .subscribe((guardianId) => {
        this.registerDeviceToken(guardianId);
      });
  }

  async hasNotificationPermissions(): Promise<PushNotificationPermissionStatus> {
    if (!Capacitor.isNativePlatform()) {
      return {
        hasPermission: false,
        message: 'PushNotifications are only available on native platforms.',
        status: 'unsupported',
      };
    }

    const permission = await PushNotifications.checkPermissions();
    switch (permission.receive) {
      case 'granted':
        return { hasPermission: true, message: 'Notification permissions are granted.', status: 'granted' };
      case 'denied':
        return { hasPermission: false, message: 'Notification permissions are disabled', status: 'denied' };
      case 'prompt':
        return { hasPermission: false, message: 'Notification permission prompt dismissed.', status: 'prompt' };
      default:
        return {
          hasPermission: false,
          message: 'Unable to determine notification permission status.',
          status: 'unsupported',
        };
    }
  }

  async openNotificationSettings(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('Native settings are only available on native platforms.');
      return;
    }

    await NativeSettings.open({
      optionAndroid: AndroidSettings.AppNotification,
      optionIOS: IOSSettings.AppNotification,
    });
  }

  async requestNotificationPermissions(): Promise<PushNotificationPermissionStatus> {
    if (!Capacitor.isNativePlatform()) {
      return { hasPermission: false, message: 'PushNotifications are only available on native platforms.' };
    }

    await this.openNotificationSettings();
    return await this.hasNotificationPermissions();
  }

  initGroupRegistration() {
    this.getExistingDeviceId()
      .pipe(
        switchMap(() =>
          this.store.select(selectLoggedInGuardianId).pipe(
            filter((id) => !!id),
            take(1),
            switchMap((guardianId) => {
              const GET_GUARDIAN_GROUPS = gql`
                query GetGuardianGroups($guardianId: String!) {
                  getGuardianGroups(guardianId: $guardianId) {
                    id
                    school
                    name
                    year
                    dateCreated
                    dateLastModified
                    createdBy
                    lastModifiedBy
                    status
                    staticGroup
                    everlyticId
                    synergyName
                    synergyType
                  }
                }
              `;

              if (!this.apollo) {
                throw new Error('Apollo client is not initialized.');
              }

              return this.apollo.query<{ getGuardianGroups: GuardianGroup[] }>({
                query: GET_GUARDIAN_GROUPS,
                variables: { guardianId },
              });
            }),
          ),
        ),
      )
      .subscribe({
        next: (result) => {
          const groups = result.data.getGuardianGroups;
          if (this.deviceId) {
            this.store
              .select(selectLoggedInGuardianId)
              .pipe(take(1))
              .subscribe((guardianId) => {
                groups.forEach((group) => {
                  // First subscribe to FCM topic locally
                  this.subscribeToFCMTopic(group.id).then(() => {
                    // Then confirm subscription with backend
                    this.subscribeToGroupNotifications(guardianId, group.id, true);
                  });
                });
              });
          } else {
            console.error('Device ID not available for group subscriptions');
          }
        },
        error: (err) => console.error('Error in group registration:', err),
      });
  }

  private async subscribeToFCMTopic(groupId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('FCM topics only work on native platforms');
      return;
    }

    try {
      const topicName = `group_${groupId}`;
      await FCM.subscribeTo({ topic: topicName });
    } catch (error) {
      console.error(`Error subscribing to FCM topic for group ${groupId}:`, error);
      throw error;
    }
  }

  private async unsubscribeFromFCMTopic(groupId: string): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      console.warn('FCM topics only work on native platforms');
      return;
    }

    try {
      const topicName = `group_${groupId}`;
      await FCM.unsubscribeFrom({ topic: topicName });
    } catch (error) {
      console.error(`Error unsubscribing from FCM topic for group ${groupId}:`, error);
      throw error;
    }
  }

  private subscribeToGroupNotifications(guardianId: string, groupId: string, subscribe: boolean) {
    const SUBSCRIBE_TO_GROUP = gql`
      mutation SubscribeToGroupNotifications($guardianId: String!, $groupId: Int!, $subscribe: Boolean!) {
        subscribeToGroupNotifications(input: { guardianId: $guardianId, groupId: $groupId, subscribe: $subscribe }) {
          id
          guardianSynergyId
          groupId
          groupName
          dateCreated
          subscribed
          dateModified
        }
      }
    `;

    if (!this.apollo) {
      console.error('Apollo client is not initialized.');
      return;
    }

    this.apollo
      .mutate({
        mutation: SUBSCRIBE_TO_GROUP,
        variables: {
          guardianId: guardianId,
          groupId: parseInt(groupId, 10),
          subscribe,
        },
      })
      .subscribe({
        error: (err) => console.error(`Error confirming subscription to group ${groupId}:`, err),
      });
  }

  registerDeviceToken(guardianId: string) {
    if (!Capacitor.isNativePlatform()) {
      console.warn('PushNotifications are only available on native platforms.');
      return;
    }

    PushNotifications.requestPermissions().then(async (permission: any) => {
      if (permission.receive !== 'granted') return;

      PushNotifications.register();

      PushNotifications.addListener('registration', async () => {
        await this.fetchAndRegisterFcmToken(guardianId);
      });

      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push registration error:', error);
      });
    });
  }

  async unregisterPushNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    try {
      await PushNotifications.removeAllListeners();

      // TODO: Deregister token on backend once a deleteMobileDeviceToken mutation exists

      await FCM.deleteInstance();
    } catch (error) {
      console.error('Error unregistering push notifications:', error);
    }

    this.listenersRegistered = false;
    this.deviceId = null;
  }

  private async fetchAndRegisterFcmToken(guardianId: string) {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        const refreshed = localStorage.getItem('fcm_instance_reset');
        if (!refreshed) {
          await FCM.deleteInstance();
          localStorage.setItem('fcm_instance_reset', 'true');
        }
      }
      const { token } = await FCM.getToken();
      await this.registerTokenWithBackend(token, guardianId);
    } catch (error) {
      console.error('Error fetching FCM token:', error);
    }
  }

  private async registerTokenWithBackend(tokenId: string, guardianId: string) {
    const info = await Device.getInfo();
    const { identifier } = await Device.getId();
    const deviceDetails = `${info.model} (${info.platform} v${info.osVersion}) - ${info.name} [${identifier}]`;
    const REGISTER_TOKEN = gql`
      mutation MobileDeviceToken($tokenId: String!, $guardianId: String!, $deviceDetails: String!) {
        mobileDeviceToken(input: { tokenId: $tokenId, guardianId: $guardianId, deviceDetails: $deviceDetails }) {
          synergyId
          tokenId
          dateCreated
          deviceDetails
          id
        }
      }
    `;

    if (!this.apollo) {
      console.error('Apollo client is not initialized.');
      return;
    }

    this.apollo
      .mutate<DeviceTokenResponse>({
        mutation: REGISTER_TOKEN,
        variables: {
          tokenId,
          guardianId,
          deviceDetails,
        },
      })
      .subscribe({
        error: (err) => console.error('Error registering device token:', err),
      });
  }

  private getExistingDeviceId() {
    return this.store.select(selectLoggedInGuardianId).pipe(
      filter((id) => !!id),
      take(1),
      switchMap((guardianId) => {
        const GET_GUARDIAN_DEVICES = gql`
          query GetGuardianDevices($guardianId: String!) {
            getGuardianDevices(guardianId: $guardianId) {
              id
              synergyId
              tokenId
              dateCreated
              deviceDetails
            }
          }
        `;

        if (!this.apollo) {
          throw new Error('Apollo client is not initialized.');
        }

        return this.apollo.query<{
          getGuardianDevices: {
            id: string;
            synergyId: string;
            tokenId: string;
            dateCreated: string;
            deviceDetails: string;
          }[];
        }>({
          query: GET_GUARDIAN_DEVICES,
          variables: { guardianId },
          fetchPolicy: 'network-only',
        });
      }),
      filter((result) => {
        if (result.data?.getGuardianDevices?.length > 0) {
          // Create a copy of the array before sorting
          const devices = [...result.data.getGuardianDevices];
          const latestDevice = devices.sort(
            (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime(),
          )[0];
          this.deviceId = latestDevice.id;
          return true;
        }
        return false;
      }),
    );
  }
}
